/* ============================================================
   SoyCandleLab — sonic branding
   ------------------------------------------------------------
   Every sound here is SYNTHESISED in the browser with Web Audio.
   There are no audio files to download, so the whole sonic layer
   costs 0 KB of bandwidth and starts instantly on a phone.

   Nothing ever plays on its own. Sound only happens as the direct
   answer to a tap:

     NAVIGATION — Home, Shop, Our Story, Contact each have their
       own note. A soft wooden tap and a short warm pluck, about
       200ms, finished before the next page starts loading.

     PRODUCTS — opening a candle plays the room that candle belongs
       to for a few seconds. Ocean gets real swelling surf, Cinnamon
       gets a fire, Australian Bush gets wind through gums.

   Anyone who has asked their device to reduce motion gets silence.
   ============================================================ */
(() => {
  'use strict';

  const quiet = matchMedia('(prefers-reduced-motion: reduce)').matches;

  let ctx = null;         // AudioContext, built on the first real tap
  let master = null;      // everything routes through here
  let noiseBuf = null;    // one shared noise buffer
  let activeScent = null; // currently playing scent bed

  const MASTER = 0.24;    // hard ceiling. Never louder than this.
  const SCENT_SECS = 4;   // a scent bed is a moment, not a track

  const now = () => ctx.currentTime;
  const rnd = (a, b) => a + Math.random() * (b - a);

  /* has to be called from inside a user gesture — iOS will not start
     an AudioContext any other way */
  function boot() {
    if (quiet) return null;
    if (ctx) { ctx.resume && ctx.resume(); return ctx; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = MASTER;
    master.connect(ctx.destination);

    const len = ctx.sampleRate * 2;
    noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    ctx.resume && ctx.resume();
    return ctx;
  }

  /* a looping noise source through a filter — wind, surf, fire */
  function noise(type, freq, q, gain) {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type; f.frequency.value = freq; f.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(f).connect(g);
    return { src, filter: f, gain: g };
  }

  function tone(type, freq, gain) {
    const o = ctx.createOscillator();
    o.type = type; o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    o.connect(g);
    return { osc: o, gain: g };
  }

  /* ---------------------------------------------------------
     NAVIGATION — one short note per destination
     ---------------------------------------------------------
     Deliberately about 200ms end to end. A longer, prettier
     chime would simply be cut off mid-decay when the next page
     starts loading, which sounds broken. Short and complete
     beats long and truncated.
     --------------------------------------------------------- */
  const NAV_NOTE = {
    'index':    329.63,  // Home — the low, settled one
    'products': 392.00,  // Shop
    'about':    493.88,  // Our Story
    'contact':  587.33,  // Contact — the brightest
  };
  const NAV_MS = 230;

  function navNote(hz) {
    if (!boot()) return;
    const t = now() + 0.005;

    // the tap: a wooden knock, felt more than heard
    const k = noise('bandpass', 620, 1.4, 0);
    k.gain.connect(master);
    k.gain.gain.setValueAtTime(0.0001, t);
    k.gain.gain.exponentialRampToValueAtTime(0.075, t + 0.006);
    k.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.085);
    k.src.start(t); k.src.stop(t + 0.12);

    // the note: soft pluck, fully decayed inside 200ms
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 2200; lp.Q.value = 0.6;
    lp.connect(master);

    const a = tone('sine', hz, 0);
    const b = tone('triangle', hz * 2.004, 0);   // detune reads as wood, not glass
    a.gain.connect(lp); b.gain.connect(lp);

    a.gain.gain.setValueAtTime(0.0001, t);
    a.gain.gain.exponentialRampToValueAtTime(0.115, t + 0.018);
    a.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.20);
    b.gain.gain.setValueAtTime(0.0001, t);
    b.gain.gain.exponentialRampToValueAtTime(0.030, t + 0.012);
    b.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.10);

    a.osc.start(t); a.osc.stop(t + 0.24);
    b.osc.start(t); b.osc.stop(t + 0.14);
  }

  /* ---------------------------------------------------------
     SCENT BEDS — the room a candle belongs to
     Gentle by design: slow fades, low peaks, nothing percussive
     that could startle someone browsing with headphones on.
     --------------------------------------------------------- */
  const BEDS = {

    /* OCEAN — swelling surf. Waves build, break, and pull back. */
    ocean(bus, dur) {
      const body = noise('lowpass', 520, 0.7, 0.0);   // the swell
      const foam = noise('bandpass', 1900, 0.9, 0.0); // the break
      body.gain.connect(bus); foam.gain.connect(bus);
      body.src.start(); foam.src.start();

      let t = now() + 0.1;
      while (t < now() + dur) {
        const rise = rnd(1.3, 1.9), fall = rnd(1.6, 2.4), peak = rnd(0.30, 0.48);
        body.gain.gain.setValueAtTime(Math.max(body.gain.gain.value, 0.0001), t);
        body.gain.gain.linearRampToValueAtTime(peak, t + rise);
        body.gain.gain.linearRampToValueAtTime(0.04, t + rise + fall);
        body.filter.frequency.setValueAtTime(360, t);
        body.filter.frequency.linearRampToValueAtTime(1000, t + rise);
        body.filter.frequency.linearRampToValueAtTime(330, t + rise + fall);
        foam.gain.gain.setValueAtTime(0.0001, t + rise * 0.55);
        foam.gain.gain.linearRampToValueAtTime(peak * 0.22, t + rise + 0.15);
        foam.gain.gain.linearRampToValueAtTime(0.0001, t + rise + fall * 0.8);
        t += rise + fall * 0.7;
      }
      return [body.src, foam.src];
    },

    /* FOREST — wind through gums, leaves turning over */
    forest(bus, dur) {
      const wind = noise('bandpass', 480, 0.6, 0.0);
      const leaf = noise('highpass', 3400, 0.5, 0.0);
      wind.gain.connect(bus); leaf.gain.connect(bus);
      wind.src.start(); leaf.src.start();
      let t = now() + 0.1;
      while (t < now() + dur) {
        const rise = rnd(1.4, 2.2), fall = rnd(1.6, 2.4), peak = rnd(0.24, 0.40);
        wind.gain.gain.setValueAtTime(Math.max(wind.gain.gain.value, 0.0001), t);
        wind.gain.gain.linearRampToValueAtTime(peak, t + rise);
        wind.gain.gain.linearRampToValueAtTime(0.04, t + rise + fall);
        wind.filter.frequency.setValueAtTime(rnd(320, 500), t);
        wind.filter.frequency.linearRampToValueAtTime(rnd(680, 900), t + rise);
        leaf.gain.gain.setValueAtTime(0.0001, t);
        leaf.gain.gain.linearRampToValueAtTime(peak * 0.09, t + rise);
        leaf.gain.gain.linearRampToValueAtTime(0.0001, t + rise + fall);
        t += rise + fall * 0.7;
      }
      return [wind.src, leaf.src];
    },

    /* HEARTH — a settled fire, for the spiced and wintry scents */
    hearth(bus, dur) {
      const ember = noise('lowpass', 300, 0.6, 0.14);
      ember.gain.connect(bus); ember.src.start();
      let t = now() + 0.05;
      while (t < now() + dur) {
        const p = noise('bandpass', rnd(900, 4200), rnd(4, 10), 0);
        p.gain.connect(bus);
        const v = rnd(0.07, 0.30);
        p.gain.gain.setValueAtTime(0.0001, t);
        p.gain.gain.exponentialRampToValueAtTime(v, t + 0.005);
        p.gain.gain.exponentialRampToValueAtTime(0.0001, t + rnd(0.04, 0.16));
        p.src.start(t); p.src.stop(t + 0.25);
        t += rnd(0.10, 0.46);
      }
      return [ember.src];
    },

    /* CITRUS — cut stems and cold glass. Bright but unhurried. */
    citrus(bus, dur) {
      const air = noise('highpass', 5200, 0.5, 0.0);
      air.gain.connect(bus); air.src.start();
      air.gain.gain.setValueAtTime(0.0001, now());
      air.gain.gain.linearRampToValueAtTime(0.05, now() + 1.2);
      const scale = [880, 1046.5, 1318.5, 1567.98];
      let t = now() + 0.25;
      while (t < now() + dur) {
        const v = tone('sine', scale[(Math.random() * scale.length) | 0], 0);
        v.gain.connect(bus);
        v.gain.gain.setValueAtTime(0.0001, t);
        v.gain.gain.exponentialRampToValueAtTime(rnd(0.04, 0.085), t + 0.02);
        v.gain.gain.exponentialRampToValueAtTime(0.0001, t + rnd(0.9, 1.6));
        v.osc.start(t); v.osc.stop(t + 1.7);
        t += rnd(0.55, 1.2);
      }
      return [air.src];
    },

    /* BLOOM — soft florals. A pad that breathes once. */
    bloom(bus, dur) {
      const kept = [];
      [261.63, 329.63, 392.00, 493.88].forEach((hz, i) => {
        const a = tone('triangle', hz, 0);
        const b = tone('sine', hz * 1.006, 0);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass'; lp.frequency.value = 700; lp.Q.value = 0.7;
        a.gain.connect(lp); b.gain.connect(lp); lp.connect(bus);
        const st = now() + i * 0.22;
        const v = 0.05 - i * 0.007;
        a.gain.gain.setValueAtTime(0.0001, st);
        a.gain.gain.linearRampToValueAtTime(v, st + 1.2);
        b.gain.gain.setValueAtTime(0.0001, st);
        b.gain.gain.linearRampToValueAtTime(v * 0.6, st + 1.5);
        lp.frequency.setValueAtTime(600, st);
        lp.frequency.linearRampToValueAtTime(1700, st + dur * 0.6);
        lp.frequency.linearRampToValueAtTime(700, st + dur);
        a.osc.start(st); b.osc.start(st);
        a.osc.stop(now() + dur + 2); b.osc.stop(now() + dur + 2);
        kept.push(a.osc, b.osc);
      });
      return kept;
    },

    /* WARM — gourmand, caramel, pear. Kitchen at dusk. */
    warm(bus, dur) {
      const kept = [];
      [130.81, 196.00, 261.63].forEach((hz, i) => {
        const o = tone('sine', hz, 0);
        o.gain.connect(bus);
        const st = now() + i * 0.32;
        o.gain.gain.setValueAtTime(0.0001, st);
        o.gain.gain.linearRampToValueAtTime(0.065 - i * 0.014, st + 1.3);
        o.osc.start(st); o.osc.stop(now() + dur + 2);
        kept.push(o.osc);
      });
      const air = noise('lowpass', 420, 0.7, 0.0);
      air.gain.connect(bus); air.src.start();
      air.gain.gain.setValueAtTime(0.0001, now());
      air.gain.gain.linearRampToValueAtTime(0.08, now() + 1.6);
      kept.push(air.src);
      return kept;
    },

    /* WATER — soaps and milk bars. Droplets into a shallow basin. */
    water(bus, dur) {
      const kept = [];
      let t = now() + 0.25;
      while (t < now() + dur) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        const top = rnd(900, 1800);
        o.frequency.setValueAtTime(top, t);
        o.frequency.exponentialRampToValueAtTime(top * 0.45, t + 0.1);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(rnd(0.05, 0.10), t + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t + rnd(0.2, 0.42));
        o.connect(g).connect(bus);
        o.start(t); o.stop(t + 0.5);
        t += rnd(0.55, 1.3);
      }
      const room = noise('bandpass', 700, 0.5, 0.0);
      room.gain.connect(bus); room.src.start();
      room.gain.gain.setValueAtTime(0.0001, now());
      room.gain.gain.linearRampToValueAtTime(0.04, now() + 1.4);
      kept.push(room.src);
      return kept;
    },

    /* CALM — essential oils, balms, serums. Almost nothing. Breath. */
    calm(bus, dur) {
      const kept = [];
      const drone = tone('sine', 174.61, 0);
      drone.gain.connect(bus);
      drone.gain.gain.setValueAtTime(0.0001, now());
      drone.gain.gain.linearRampToValueAtTime(0.055, now() + 1.6);
      drone.osc.start(); drone.osc.stop(now() + dur + 2);
      kept.push(drone.osc);
      const breath = noise('bandpass', 620, 0.8, 0.0);
      breath.gain.connect(bus); breath.src.start();
      breath.gain.gain.setValueAtTime(0.0001, now());
      breath.gain.gain.linearRampToValueAtTime(0.07, now() + dur * 0.45);
      breath.gain.gain.linearRampToValueAtTime(0.01, now() + dur);
      kept.push(breath.src);
      return kept;
    },
  };

  /* ---- which bed belongs to which product ------------------ */
  const BY_HANDLE = {
    'ocean': 'ocean',
    'amalfi-coast': 'ocean',
    'coconut-margarita': 'ocean',
    'australian-bush': 'forest',
    /* incense, not eucalyptus — cedarwood/musk/masala/spices reads as
       ember and smoke, not wind through trees */
    'melbourne-botantical': 'forest',
    /* notes are eucalyptus/pine-needle/sandalwood — near-identical to
       australian-bush's own notes, not a citrus profile. Lemon is only
       the top note. */
    'lemon-myrtle-eucalyptus': 'forest',
    'lemongrass': 'citrus',
    'gin-spice': 'citrus',
    'lotus': 'bloom',
    'jasmine-gardenia': 'bloom',
    'romance': 'bloom',
    /* notes are papaya/pineapple/mango/coconut — a tropical-fruit profile
       almost identical to coconut-margarita's, not a true floral despite
       "honeysuckle" in the name */
    'golden-honeysuckel': 'ocean',
    'strawberries-champagne': 'bloom',
    'petal-candle': 'bloom',
    'french-pear': 'warm',
    'caramel': 'warm',
    'cinnamon': 'hearth',
    'pumpkin-spice': 'hearth',
    'christmas': 'hearth',
    /* 2026-09-30 new scents: bed follows the notes, not the name */
    'black-raspberry': 'warm',
    'paw-paw-passion': 'ocean',
    'sandalwood': 'hearth',
    'lychee-guava': 'ocean',
    'vanilla-chai': 'warm',
    'vanilla-tobacco': 'hearth',
    'summer-watermelon': 'citrus',
    'warm-amber': 'hearth',
    'lavender-ylang-ylang': 'bloom',
    'beeswax-pillar-candles': 'hearth',
    'wick-trimmer': 'hearth',
    'aroma-therapy-trio': 'calm',
    /* smoky resinous wood ("if smoke is not for you...") — hearth's
       ember/smoke reads true, the aromatherapy default (calm) doesn't */
    'palo-santo-cleansing-room-spray': 'hearth',
    /* the three rollers are named for three different moods — giving them
       all the same calm breath-pad erases exactly what they're sold on */
    'purify-essential-oil-roller': 'forest',
    'soothe-essential-oil-roller': 'calm',
  };

  /* fallback by category + wording, so new products still get a room */
  function bedFor(p) {
    if (!p) return 'hearth';
    if (BY_HANDLE[p.handle]) return BY_HANDLE[p.handle];
    const txt = `${p.handle} ${p.title} ${p.desc || ''} ${Object.values(p.notes || {}).join(' ')}`.toLowerCase();

    /* Bath and aromatherapy items are named by INGREDIENT (coconut milk,
       goats milk) or FUNCTION, not by fragrance family — a "coconut milk
       soap" is a soap base, not a coconut-scented candle. Left to the
       candle-scent sweep below, "coconut" pulled every soap into the ocean
       bed, "lemongrass"/"orange" pulled them into citrus, and "Palo Santo
       Soap" into forest — none of them ever reached the water bed they
       were supposed to get. Category (and a plain "soap" check, which also
       catches the assorted soap set) has to be decided before the candle
       sweep runs, not after. */
    if (/soap|wash|scrub/.test(txt)) return 'water';
    if (p.cat === 'bath-body') return 'water';
    if (p.cat === 'aromatherapy') return /santo|spray|mist|room/.test(txt) ? 'hearth' : 'calm';

    /* candles only from here — matched by actual fragrance notes */
    if (/ocean|sea|salt|coast|marine|surf|beach|coconut|margarita/.test(txt)) return 'ocean';
    if (/eucalypt|bush|forest|pine|cedar|sandalwood|timber|moss|santo|charcoal/.test(txt)) return 'forest';
    if (/lemon|lime|citrus|orange|grass|mint|pepperm|bergamot|myrtle/.test(txt)) return 'citrus';
    if (/rose|jasmin|floral|petal|gardenia|lotus|honeysuck|lavender|blossom/.test(txt)) return 'bloom';
    if (/cinnam|spice|clove|smoke|amber|tobacco|christmas|pumpkin|fire/.test(txt)) return 'hearth';
    if (/vanilla|caramel|pear|sugar|honey|choc|berry|cake|butter/.test(txt)) return 'warm';
    return 'hearth';
  }

  /* play a scent bed, fading gently in and out around it */
  function playScent(name, secs = SCENT_SECS) {
    if (!boot() || !BEDS[name]) return;
    stopScent();
    const bus = ctx.createGain();
    bus.gain.value = 0;
    bus.connect(master);
    const nodes = BEDS[name](bus, secs) || [];

    /* long fades at both ends — the bed should arrive and leave
       without a moment where it obviously starts or obviously stops */
    bus.gain.setValueAtTime(0.0001, now());
    bus.gain.linearRampToValueAtTime(1, now() + 0.85);
    bus.gain.setValueAtTime(1, now() + secs - 1.3);
    bus.gain.linearRampToValueAtTime(0.0001, now() + secs);

    const kill = setTimeout(() => {
      nodes.forEach(n => { try { n.stop(); } catch (e) {} });
      try { bus.disconnect(); } catch (e) {}
      if (activeScent && activeScent.kill === kill) activeScent = null;
    }, (secs + 0.4) * 1000);

    activeScent = { bus, nodes, kill, name };
  }

  function stopScent() {
    if (!activeScent) return;
    const a = activeScent; activeScent = null;
    clearTimeout(a.kill);
    try { a.bus.gain.cancelScheduledValues(now()); } catch (e) {}
    a.bus.gain.setTargetAtTime(0.0001, now(), 0.16);
    setTimeout(() => {
      a.nodes.forEach(n => { try { n.stop(); } catch (e) {} });
      try { a.bus.disconnect(); } catch (e) {}
    }, 700);
  }

  /* ---------------------------------------------------------
     wire the navigation
     ---------------------------------------------------------
     Every internal page link gets its destination's note. The
     jump is held for NAV_MS so the note finishes rather than
     being chopped in half by the page unloading — short enough
     that it reads as the link responding, not as lag.
     --------------------------------------------------------- */
  function pageKey(href) {
    const m = /([a-z0-9-]+)\.html/i.exec(href || '');
    return m ? m[1].toLowerCase() : null;
  }

  document.addEventListener('click', (e) => {
    if (quiet) return;
    const a = e.target.closest('a[href]');
    if (!a) return;
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
        e.button !== 0 || a.target === '_blank' || a.hasAttribute('download')) return;

    const href = a.getAttribute('href') || '';
    if (/^(#|mailto:|tel:)/.test(href)) return;

    let url;
    try { url = new URL(a.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin) return;      // offsite: let it go

    const key = pageKey(url.pathname);
    if (!key || !(key in NAV_NOTE)) return;

    navNote(NAV_NOTE[key]);

    // same page, different filter (products.html?c=…): no need to wait
    if (url.pathname === location.pathname && url.search === location.search) return;

    e.preventDefault();
    setTimeout(() => { location.href = a.href; }, NAV_MS);
  }, true);

  /* stop making noise at a tab nobody is looking at */
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) { stopScent(); ctx.suspend(); } else ctx.resume();
  });

  /* ---------------------------------------------------------
     public hooks — main.js calls these
     --------------------------------------------------------- */
  window.SCLSound = {
    get muted() { return quiet; },
    /* the room this product belongs to */
    product(p, secs) { playScent(bedFor(p), secs || SCENT_SECS); },
    bed(name, secs) { playScent(name, secs || SCENT_SECS); },
    stop: stopScent,
    /* a wick catch — used on quiz answers */
    tick() {
      if (!boot()) return;
      const t = now();
      const p = noise('bandpass', rnd(1400, 2400), 6, 0);
      p.gain.connect(master);
      p.gain.gain.setValueAtTime(0.0001, t);
      p.gain.gain.exponentialRampToValueAtTime(0.075, t + 0.006);
      p.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.10);
      p.src.start(t); p.src.stop(t + 0.16);
    },
    /* peak level off the master bus over `ms`, 0..1 — lets us confirm
       sound is actually reaching the output rather than assuming it */
    level(ms = 600) {
      if (!ctx || !master) return Promise.resolve(0);
      const an = ctx.createAnalyser();
      an.fftSize = 2048;
      master.connect(an);
      const buf = new Float32Array(an.fftSize);
      let peak = 0;
      return new Promise(res => {
        const t0 = performance.now();
        const id = setInterval(() => {
          an.getFloatTimeDomainData(buf);
          for (let i = 0; i < buf.length; i++) {
            const v = Math.abs(buf[i]); if (v > peak) peak = v;
          }
          if (performance.now() - t0 >= ms) {
            clearInterval(id);
            try { master.disconnect(an); } catch (e) {}
            res(+peak.toFixed(4));
          }
        }, 16);
      });
    },
    _nav(key) { navNote(NAV_NOTE[key]); },
  };
})();
